import {
    CANCEL_CHANGES_REGIONS_EDITOR,
    CancelChangesRegionsEditor,
    CHANGE_FILTER_TEXT_REGIONS_EDITOR,
    ChangeFilterTextRegionsEditor,
    CLEAR_SELECTED_ITEMS_REGIONS_EDITOR,
    ClearSelectedRegionsEditorItems,
    CLOSE_REGIONS_EDITOR,
    CloseRegionsEditor,
    COLLAPSE_REGIONS_EDITOR_ITEM,
    CollapseRegionsEditorItem,
    DESELECT_QUICK_REGIONS_EDITOR_ITEM,
    DESELECT_REGIONS_EDITOR_ITEM,
    DeselectQuickRegionsEditorItem,
    DeselectRegionsEditorItem,
    EXPAND_REGIONS_EDITOR_ITEM,
    ExpandRegionsEditorItem,
    OPEN_REGIONS_EDITOR,
    OpenRegionsEditor,
    RegionsEditorAction,
    RESET_REGIONS_EDITOR,
    SELECT_QUICK_REGIONS_EDITOR_ITEM,
    SELECT_REGIONS_EDITOR_ITEM,
    SelectQuickRegionsEditorItem,
    SelectRegionsEditorItem,
    START_CHANGE_FILTER_TEXT_REGIONS_EDITOR,
    StartChangeFilterTextRegionsEditor
} from '../actions/regionsEditor';
import { TreeHelpers } from '../service/TreeHelpers/TreeHelpers';
import { Region } from '../typings/common';
import { GroupId } from '../typings/groups';
import { RegionsEditorState } from '../typings/regionsEditor';
import { State } from '../typings/state';

type RegionsEditorsState = Record<GroupId, RegionsEditorState>;

export const defaultRegionsEditorData = {
    selectedMap: {},
    expandedMap: {},
    isOpen: false,
    searchValue: ''
};

const openRegionsEditor = (
    state: RegionsEditorsState,
    action: OpenRegionsEditor
): RegionsEditorsState => {
    const mapIdsAllRegions = TreeHelpers.updateMapForCurrentIds(
        TreeHelpers.getChildNodesIds(action.tree),
        {},
        true
    );

    const listFirstSelectedIds = action.tree.reduce((acc: string[], node) => {
        const foundedNodes = TreeHelpers.getFirstEqualsTreeNodesList(
            node,
            action.selectedMap,
            true
        );
        acc = acc.concat(foundedNodes.map(foundedNode => foundedNode.id));

        return acc;
    }, []);

    const listExpandedIds = TreeHelpers.getIdsPathToCurrentNodes(
        action.tree,
        listFirstSelectedIds
    );

    const expandedMap = TreeHelpers.updateMapForCurrentIds(
        listExpandedIds,
        {},
        true
    );

    return {
        ...state,
        [action.id]: {
            ...defaultRegionsEditorData,
            filteredMap: mapIdsAllRegions,
            expandedMap: expandedMap,
            isOpen: true,
            selectedMap: action.selectedMap
        }
    };
};

const selectRegionsEditorItem = (
    state: RegionsEditorsState,
    action: SelectRegionsEditorItem
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const { selectedMap, expandedMap } = currentRegionsEditor;

    const listIdsForCurrentNode = TreeHelpers.getChildNodesIds([action.node]);
    const newSelectedMap = TreeHelpers.updateMapForCurrentIds(
        listIdsForCurrentNode,
        selectedMap,
        true
    );
    const newExpandedMap = TreeHelpers.updateMapForCurrentIds(
        [action.node.id],
        expandedMap,
        true
    );

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: newSelectedMap,
            expandedMap: newExpandedMap
        }
    };
};

const deselectRegionsEditorItem = (
    state: RegionsEditorsState,
    action: DeselectRegionsEditorItem
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const { selectedMap } = currentRegionsEditor;

    const listIdsForCurrentNode = TreeHelpers.getChildNodesIds([action.node]);
    const newSelectedMap = TreeHelpers.updateMapForCurrentIds(
        listIdsForCurrentNode,
        selectedMap,
        false
    );

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: newSelectedMap
        }
    };
};

const expandRegionsEditorItem = (
    state: RegionsEditorsState,
    action: ExpandRegionsEditorItem
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const { expandedMap } = currentRegionsEditor;

    const newExpandedMap = TreeHelpers.updateMapForCurrentIds(
        [action.node.id],
        expandedMap,
        true
    );

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            expandedMap: newExpandedMap
        }
    };
};

const collapseRegionsEditorItem = (
    state: RegionsEditorsState,
    action: CollapseRegionsEditorItem
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const { expandedMap } = currentRegionsEditor;

    const newExpandedMap = TreeHelpers.updateMapForCurrentIds(
        [action.node.id],
        expandedMap,
        false
    );

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            expandedMap: newExpandedMap
        }
    };
};

const selectQuickRegionsEditorItem = (
    state: RegionsEditorsState,
    action: SelectQuickRegionsEditorItem
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const { expandedMap, selectedMap } = currentRegionsEditor;

    const expandedListIds = TreeHelpers.getIdsPathToCurrentNodes(
        action.tree,
        action.nodeIds
    );
    const listIdsForCurrentNodes = action.nodeIds.reduce(
        (acc: string[], nodeId) => {
            const node = TreeHelpers.getNodeById<Region>(action.tree, nodeId);

            if (node) {
                acc = acc.concat(TreeHelpers.getChildNodesIds([node]));
            }

            return acc;
        },
        []
    );

    const newSelectedMap = TreeHelpers.updateMapForCurrentIds(
        listIdsForCurrentNodes,
        selectedMap,
        true
    );
    const newExpandedMap = TreeHelpers.updateMapForCurrentIds(
        expandedListIds,
        expandedMap,
        true
    );

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: newSelectedMap,
            expandedMap: newExpandedMap,
            idForScrolling: action.nodeIds[0]
        }
    };
};

const deselectQuickRegionsEditorItem = (
    state: RegionsEditorsState,
    action: DeselectQuickRegionsEditorItem
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const { selectedMap } = currentRegionsEditor;

    const listIdsForCurrentNodes = action.nodeIds.reduce(
        (acc: string[], nodeId) => {
            const nodeItem = TreeHelpers.getNodeById<Region>(
                action.tree,
                nodeId
            );

            if (nodeItem) {
                acc = acc.concat(TreeHelpers.getChildNodesIds([nodeItem]));
            }

            return acc;
        },
        []
    );

    const newSelectedMap = TreeHelpers.updateMapForCurrentIds(
        listIdsForCurrentNodes,
        selectedMap,
        false
    );

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: newSelectedMap,
            idForScrolling: action.nodeIds[0]
        }
    };
};

const changeFilterTextRegionsEditor = (
    state: RegionsEditorsState,
    action: ChangeFilterTextRegionsEditor
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];
    const mapIdsAllRegions = TreeHelpers.updateMapForCurrentIds(
        TreeHelpers.getChildNodesIds(action.tree),
        {},
        true
    );

    if (action.value.length === 0) {
        return {
            ...state,
            [action.id]: {
                ...currentRegionsEditor,
                filteredMap: mapIdsAllRegions
            }
        };
    }

    const {
        filteredMap,
        expandedMap,
        listFoundedIds
    } = TreeHelpers.getUpdatedBySearchTreeParams(action.tree, action.value);

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            expandedMap,
            filteredMap,
            idForScrolling: listFoundedIds[0]
        }
    };
};

const startChangeFilterTextRegionsEditor = (
    state: RegionsEditorsState,
    action: StartChangeFilterTextRegionsEditor
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            searchValue: action.value
        }
    };
};

const clearSelectedRegionsEditorItems = (
    state: RegionsEditorsState,
    action: ClearSelectedRegionsEditorItems
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: {}
        }
    };
};

const cancelChangesRegionsEditor = (
    state: RegionsEditorsState,
    action: CancelChangesRegionsEditor
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            selectedMap: action.selectedMap,
            isOpen: false
        }
    };
};

const closeRegionsEditor = (
    state: RegionsEditorsState,
    action: CloseRegionsEditor
): RegionsEditorsState => {
    const currentRegionsEditor = state[action.id];

    return {
        ...state,
        [action.id]: {
            ...currentRegionsEditor,
            isOpen: false
        }
    };
};

export function regionsEditor(
    state: RegionsEditorsState = {},
    action: RegionsEditorAction,
    fullState: State
): RegionsEditorsState {
    switch (action.type) {
        case OPEN_REGIONS_EDITOR:
            return openRegionsEditor(state, action);
        case SELECT_REGIONS_EDITOR_ITEM:
            return selectRegionsEditorItem(state, action);
        case DESELECT_REGIONS_EDITOR_ITEM:
            return deselectRegionsEditorItem(state, action);
        case EXPAND_REGIONS_EDITOR_ITEM:
            return expandRegionsEditorItem(state, action);
        case COLLAPSE_REGIONS_EDITOR_ITEM:
            return collapseRegionsEditorItem(state, action);
        case SELECT_QUICK_REGIONS_EDITOR_ITEM:
            return selectQuickRegionsEditorItem(state, action);
        case DESELECT_QUICK_REGIONS_EDITOR_ITEM:
            return deselectQuickRegionsEditorItem(state, action);
        case START_CHANGE_FILTER_TEXT_REGIONS_EDITOR:
            return startChangeFilterTextRegionsEditor(state, action);
        case CHANGE_FILTER_TEXT_REGIONS_EDITOR:
            return changeFilterTextRegionsEditor(state, action);
        case CLEAR_SELECTED_ITEMS_REGIONS_EDITOR:
            return clearSelectedRegionsEditorItems(state, action);
        case CANCEL_CHANGES_REGIONS_EDITOR:
            return cancelChangesRegionsEditor(state, action);
        case CLOSE_REGIONS_EDITOR:
            return closeRegionsEditor(state, action);
        case RESET_REGIONS_EDITOR:
            return {};
        default:
            return state;
    }
}
